import type { Document, ViewMode } from '../types';
import { people } from '../data';
import { ChevronRight, EditDoc, Eye, Panel, Plus, Refresh, Share, Sparkle } from './DocIcons';

interface Props {
  doc: Document;
  mode: ViewMode;
  onMode: (m: ViewMode) => void;
  aiOpen: boolean;
  onToggleAI: () => void;
  onGenerateDoc: () => void;
  generating: boolean;
  collaborators: string[];
}

function Avatar({ personId, size = 28, ring }: { personId: string; size?: number; ring?: boolean }) {
  const p = people[personId] ?? { initials: '?', hue: 258, isAI: false };
  return (
    <div title={p.name} style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'grid', placeItems: 'center', fontSize: size * 0.38, fontWeight: 700,
      fontFamily: 'var(--mono)', color: 'white', letterSpacing: '-0.02em',
      background: p.isAI ? 'var(--ai)' : `oklch(0.6 0.14 ${p.hue})`,
      boxShadow: ring ? '0 0 0 2px var(--surface)' : 'none',
    }}>{p.initials}</div>
  );
}

const STATUS_MAP: Record<string, [string, string, string]> = {
  approved: ['Approved', 'var(--good-soft)', 'var(--good)'],
  'in-review': ['In review', 'var(--warn-soft)', 'oklch(0.5 0.13 70)'],
  draft: ['Draft', 'var(--bg-2)', 'var(--muted)'],
};

function StatusPill({ status }: { status: string }) {
  const [label, bg, fg] = STATUS_MAP[status] ?? STATUS_MAP.draft;
  return <span className="doc-chip" style={{ background: bg, color: fg }}>{label}</span>;
}

export function DocTopBar({ doc, mode, onMode, aiOpen, onToggleAI, onGenerateDoc, generating, collaborators }: Props) {
  return (
    <header style={{
      height: 54, borderBottom: '1px solid var(--line)', background: 'var(--surface)',
      display: 'flex', alignItems: 'center', gap: 12, padding: '0 14px 0 18px', flexShrink: 0, zIndex: 20,
    }}>
      {/* breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 13, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{doc.category}</span>
        <ChevronRight size={13} style={{ color: 'var(--faint)', flexShrink: 0 }} />
        <span style={{ fontSize: 13, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{doc.type}</span>
        <ChevronRight size={13} style={{ color: 'var(--faint)', flexShrink: 0 }} />
        <span style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 }}>{doc.title}</span>
        <span className="doc-chip" style={{ background: 'var(--bg-2)', color: 'var(--muted)', marginLeft: 2 }}>{doc.code}</span>
        <StatusPill status={doc.status} />
      </div>

      {/* view/edit toggle */}
      <div style={{ display: 'flex', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', padding: 2, flexShrink: 0 }}>
        {([['view', Eye, 'View'], ['edit', EditDoc, 'Edit']] as const).map(([m, Icon, label]) => (
          <button key={m} onClick={() => onMode(m as ViewMode)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, height: 26, padding: '0 11px', border: 'none',
              borderRadius: 5, fontWeight: 600, fontSize: 12.5, cursor: 'pointer', fontFamily: 'var(--sans)',
              background: mode === m ? 'var(--surface)' : 'transparent',
              color: mode === m ? 'var(--ink)' : 'var(--muted)',
              boxShadow: mode === m ? 'var(--shadow-sm)' : 'none',
            }}>
            <Icon size={14} />{label}
          </button>
        ))}
      </div>

      {/* collaborators */}
      <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 4, flexShrink: 0 }}>
        <div style={{ display: 'flex' }}>
          {collaborators.map((p, i) => (
            <div key={p} style={{ marginLeft: i ? -7 : 0 }}><Avatar personId={p} size={28} ring /></div>
          ))}
        </div>
        <div style={{
          width: 28, height: 28, marginLeft: -7, borderRadius: '50%', border: '1.5px dashed var(--line)',
          background: 'var(--surface)', display: 'grid', placeItems: 'center', color: 'var(--muted)',
          boxShadow: '0 0 0 2px var(--surface)',
        }}>
          <Plus size={13} />
        </div>
      </div>

      <div style={{ width: 1, height: 26, background: 'var(--line)', margin: '0 2px', flexShrink: 0 }} />

      <button className="doc-btn sm" style={{ flexShrink: 0 }}>
        <Share size={15} />Share
      </button>
      <button className="doc-btn ai sm" onClick={onGenerateDoc} disabled={generating} style={{ flexShrink: 0 }}>
        {generating ? <Refresh size={15} className="doc-spin" /> : <Sparkle size={15} />}
        {generating ? 'Generating…' : 'Generate document'}
      </button>
      <button
        className={'doc-icon-btn' + (aiOpen ? ' on' : '')}
        onClick={onToggleAI}
        title="Toggle AI panel"
        style={{ flexShrink: 0 }}
      >
        <Panel size={17} />
      </button>
    </header>
  );
}
